import React, { useEffect, useId, useRef, useState } from 'react';
import { useT } from '../../contexts/LocaleContext.jsx';
import { useFetchWrapper } from '../../_helpers/fetchWrapper.js';
import config from '../../config.js';

/**
 * "Free requests" on/off in band settings (ENG-665): whether listeners may
 * request songs without paying (`/bands/{id}/free-requests`, ENG-664, members
 * only). Off for a new band. Saved as soon as it is flipped; a failed save
 * flips back and says so.
 */
const FreeRequestsToggle = ({ bandId }) => {
  const t = useT();
  const fetchWrapperRef = useRef(useFetchWrapper());
  const id = useId();
  const [enabled, setEnabled] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!bandId) return undefined;
    let cancelled = false;
    fetchWrapperRef.current
      .get(`${config.apiBaseUrl}/bands/${bandId}/free-requests`)
      .then((answer) => { if (!cancelled) setEnabled(Boolean(answer?.enabled)); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [bandId]);

  const flip = async (next) => {
    const prev = enabled;
    setEnabled(next);
    setSaving(true);
    setError(false);
    try {
      const answer = await fetchWrapperRef.current.put(`${config.apiBaseUrl}/bands/${bandId}/free-requests`, { enabled: next });
      setEnabled(Boolean(answer?.enabled));
    } catch (_err) {
      setEnabled(prev);
      setError(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="form-section free-requests-setting" data-testid="free-requests-setting">
      <label className="form-label free-requests-label" htmlFor={id}>
        <input
          id={id}
          type="checkbox"
          checked={Boolean(enabled)}
          disabled={enabled === null || saving}
          onChange={(e) => flip(e.target.checked)}
        />
        {t('bandSettings.freeRequests.label')}
      </label>
      <p className="free-requests-help">{t('bandSettings.freeRequests.help')}</p>
      {error && <p className="message error-message" role="alert">{t('bandSettings.freeRequests.failed')}</p>}
    </div>
  );
};

export default FreeRequestsToggle;
